import { useEffect, useState, useRef, useMemo } from "react";
import {
  SvgLogo,
  SvgPlay,
  SvgPause,
  Svg1X,
  Svg05X,
  Svg2X,
  Svg4X,
  SvgPlayback,
  SvgClockTime,
  SvgDownload,
  SvgFullscreen,
  SvgStreamingLayout,
  SvgGrid,
  SvgAlertPrevPage,
  SvgAlertNextPage,
} from "ui/svgs";
import clsx from "clsx";
import { useService } from "pages/dashboard/alerts/service";
import { Popover } from "react-tiny-popover";
import { Dropdown, Slider, Tooltip, Spin } from "antd";
import moment from "moment";
import { softTitle, downloadFromSrc } from "lib/utils";
import type { SliderSingleProps } from "antd";
import PlaybackModal from "ui/playback/playback-modal";
import { useDebouncedCallback } from "use-debounce";

export default function GridPage({
  selectedCamera,
  curPreset,
  startEnd,
  setStartEnd,
  progressData,
  progressDataLoading,
}) {
  const refPlayerContainer = useRef(null);
  const containerRef = useRef(undefined);
  const { getCachedImg, setCachedImg } = useService();
  const [pause, setPause] = useState(true);
  const [playSpeed, setPlaySpeed] = useState(4);

  const [playbackModalVisible, setPlaybackModalVisible] = useState(false);
  const [showStartEndSlider, setShowStartEndSlider] = useState(false);
  const [isGridPopoverOpen, setIsGridPopoverOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [eventImgMap, setEventImgMap] = useState(new Map());
  const [currentPage, setCurrentPage] = useState(0);
  const [showData, setShowData] = useState([]);
  const [layout, setLayout] = useState(4);
  const [curEventData, setCurEventData] = useState(null);
  const layoutRef = useRef(2);
  const currentPageRef = useRef(0);

  useEffect(() => {
    setCurEventData(null);
    setPause(true);
    setCurrentPage(0);
    currentPageRef.current = 0;
    setEventImgMap(new Map());
    progressData?.map((data) => {
      async function downloadImageAsBlob(imageUrl, retry = 0) {
        try {
          const response = await fetch(imageUrl);
          if (!response.ok) {
            throw new Error("Fail to download image");
          }

          const blob = await response.blob();
          setEventImgMap(
            (prev) =>
              new Map(prev.set(data.eventId, URL.createObjectURL(blob))),
          );
          setCachedImg(data.eventId, "presetGuardPic", blob);
        } catch (error) {
          if (retry >= 3) {
            console.warn("Fail to download event image", error);
            return;
          }
          downloadImageAsBlob(imageUrl, retry + 1);
        }
      }
      getCachedImg(data.eventId, "presetGuardPic").then((blobData) => {
        if (blobData) {
          setEventImgMap(
            (prev) =>
              new Map(prev.set(data.eventId, URL.createObjectURL(blobData))),
          );
        } else {
          downloadImageAsBlob(data.snapshotUrl);
        }
      });
    });
  }, [progressData]);

  // when layout changed, update current
  useEffect(() => {
    const oldLayout = layoutRef.current;
    layoutRef.current = layout;
    const newCurrent = Math.floor(
      (currentPage * oldLayout * oldLayout) / (layout * layout),
    );
    setCurrentPage(newCurrent);
    currentPageRef.current = newCurrent;
  }, [layout]);

  useEffect(() => {
    setShowData(
      progressData.slice(
        currentPage * layout * layout,
        (currentPage + 1) * layout * layout,
      ),
    );
  }, [progressData, currentPage, layout]);

  useEffect(() => {
    if (pause || progressData.length == 0) return;
    const doLoop = () => {
      const maxPage =
        Math.ceil(
          progressData.length / (layoutRef.current * layoutRef.current),
        ) - 1;
      if (currentPageRef.current === maxPage) {
        setPause(true);
        return;
      }
      currentPageRef.current++;
      setCurrentPage(currentPageRef.current);
    };
    doLoop();
    const timer = setInterval(doLoop, 4000 / playSpeed);
    return () => {
      clearTimeout(timer);
    };
  }, [pause, playSpeed, progressData]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  function handlePauseResume() {
    setPause(!pause);
  }

  function speedChange(speed) {
    setPlaySpeed(speed);
  }

  function renderSpeedIcon() {
    switch (playSpeed) {
      case 0.5:
        return <Svg05X />;
      case 1:
        return <Svg1X />;
      case 2:
        return <Svg2X />;
      case 4:
        return <Svg4X />;
    }
  }

  const marks: SliderSingleProps["marks"] = {
    0: "0",
    3: "3",
    6: "6",
    9: "9",
    12: "12",
    15: "15",
    18: "18",
    21: "21",
    24: "24",
  };

  const onSliderChange = useDebouncedCallback((values) => {
    if (values?.length < 2) {
      return;
    }
    setStartEnd(values);
  }, 500);

  function convertHourToAmPm(hour: number): string {
    if (hour === 0 || hour === 24) {
      return "12 AM";
    } else if (hour === 12) {
      return "12 PM";
    } else if (hour < 12) {
      return `${hour} AM`;
    } else {
      return `${hour - 12} PM`;
    }
  }

  function handleDownloadPresetPic() {
    const formattedName = moment(Number(curEventData?.createdAt)).format(
      "MM-DD hh_mm A",
    );
    const fileName = `${selectedCamera?.name}-${formattedName}.jpg`;
    downloadFromSrc(fileName, eventImgMap?.get(curEventData.eventId));
  }

  function handleFullscreen(elem) {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullScreen(false);
    } else {
      setIsFullScreen(true);
      elem?.focus();
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        // Firefox
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        // Chrome, Safari, and Opera
        elem.webkitRequestFullscreen();
      }
    }
  }

  const onLayoutChange = (layout) => {
    setLayout(layout);
  };

  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
    currentPageRef.current--;
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
    currentPageRef.current++;
  };

  function renderExtraLayout() {
    const elements = [];
    const totalPageNum = Math.ceil(progressData.length / (layout * layout));
    const totalCameraPlaceholderNum = totalPageNum * layout * layout;
    const validPlaceholderNum = totalCameraPlaceholderNum - progressData.length;
    for (let i = 0; i < validPlaceholderNum; i++) {
      elements.push(
        <div
          className={clsx(
            "aspect-video  w-full border-4  flex flex-col items-center justify-center",
            isFullScreen
              ? "bg-[#1a1a1a] border-black"
              : "bg-zinc-100 border-white",
          )}
          key={"extra" + i}
        >
          <SvgLogo width="60" className="fill-neutral-400 mb-3" />
          <p className="text-neutral-400 font-semibold text-2xl">
            {softTitle()}
          </p>
        </div>,
      );
    }
    return elements;
  }

  const hasNextPage =
    currentPage <
    Math.ceil(progressData.length / (layoutRef.current * layoutRef.current)) -
      1;

  if (!curPreset) {
    return (
      <div className="flex-grow flex items-center justify-center text-lg text-zinc-500">
        Please select a preset
      </div>
    );
  }

  return (
    <>
      <div className="p-6 flex-grow overflow-x-hidden">
        <div className="h-full flex flex-col gap-4 overflow-x-hidden">
          <div className="flex-grow w-full  overflow-hidden flex items-center justify-center relative object-cover p-1">
            {progressDataLoading ? (
              <div className="w-full h-full flex justify-center items-center">
                <Spin size="large" />
              </div>
            ) : progressData.length === 0 ? (
              <div className="flex items-center justify-center w-full h-full text-lg text-zinc-500">
                No data available
              </div>
            ) : (
              <div
                className={clsx(
                  "h-full aspect-video max-w-full max-h-full flex items-center justify-center relative",
                )}
                ref={refPlayerContainer}
                tabIndex={0}
              >
                <div
                  className={clsx(
                    "grid gap-0 h-full max-h-full max-w-full mx-auto aspect-video",
                    "grid-cols-" + layout,
                  )}
                >
                  {showData.map((item) => {
                    const selected = item?.eventId === curEventData?.eventId;
                    return eventImgMap?.has(item?.eventId) ? (
                      <div
                        className={clsx(
                          "relative overflow-hidden",
                          selected
                            ? "border-4 border-[#3E83F6]"
                            : "border-4 border-transparent",
                        )}
                        onClick={() => {
                          setCurEventData(item);
                        }}
                      >
                        <img
                          src={eventImgMap.get(item?.eventId)}
                          id={item?.eventId}
                          alt="Preset Event Snapshot"
                          className="aspect-video w-full max-h-full relative select-none"
                          onDoubleClick={() => {
                            const elem = document.getElementById(item?.eventId);
                            handleFullscreen(elem);
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className={clsx(
                          "aspect-video  w-full border-4  flex flex-col items-center justify-center",
                          isFullScreen
                            ? "bg-[#1a1a1a] border-black"
                            : "bg-zinc-100 border-white",
                        )}
                      >
                        <SvgLogo width="80" className="fill-neutral-400" />
                        <p className="text-neutral-400 font-semibold text-2xl">
                          {softTitle()}
                        </p>
                      </div>
                    );
                  })}
                  {currentPage + 1 ==
                    Math.ceil(progressData.length / (layout * layout)) &&
                    renderExtraLayout()}
                </div>
              </div>
            )}
          </div>
          <div
            ref={containerRef}
            className=" h-[86px] w-full bg-[#EEEEEE] rounded-2xl flex justify-center items-center gap-2 pl-2 pr-4 flex-shrink-0"
          >
            <Popover
              //placement="top"
              //color="white"
              isOpen={showStartEndSlider}
              positions={["top", "bottom", "left", "right"]}
              onClickOutside={() => setShowStartEndSlider(false)}
              content={
                <div className="px-4 py-2 rounded-2xl w-64 bg-[#ffffff] border shadow progressTimeSlider">
                  <Slider
                    range
                    defaultValue={startEnd}
                    //value={startEnd}
                    marks={marks}
                    max={24}
                    styles={{
                      rail: { background: "#e1e1e1" },
                      track: { background: "#3e83f6" },
                      handle: {
                        background: "#3e83f6",
                        borderColor: "#3e83f6",
                      },
                    }}
                    tooltip={{
                      formatter: (value) => {
                        if (value == 24) {
                          return "23:59";
                        }
                        return `${value}:00`;
                      },
                    }}
                    onChange={onSliderChange}
                  />
                </div>
              }
            >
              <button
                className="w-[150px] h-[35px] border border-[#9E9E9E] font-semibold flex-shrink-0 flex gap-1 items-center text-sm justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent"
                onClick={() => setShowStartEndSlider(true)}
              >
                <SvgClockTime width="17" className="fill-zinc-500" />
                {convertHourToAmPm(startEnd?.at(0))}
                {" - "}
                {convertHourToAmPm(startEnd?.at(1))}
              </button>
            </Popover>
            <button
              disabled={progressData?.length === 0}
              className="w-[60px] h-[60px] flex-shrink-0 flex items-center justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:fill-neutral-400 disabled:cursor-not-allowed"
              onClick={handlePauseResume}
            >
              {pause ? <SvgPlay width="28" /> : <SvgPause width="28" />}
            </button>
            <Dropdown
              overlayClassName="virtualGuardProgress__speedDropdown"
              menu={{
                items: [
                  {
                    label: "1x",
                    key: 2,
                    onClick: () => speedChange(1),
                  },
                  {
                    label: "2x",
                    key: 3,
                    onClick: () => speedChange(2),
                  },
                  {
                    label: "4x",
                    key: 4,
                    onClick: () => speedChange(4),
                  },
                ],
              }}
              placement="top"
              arrow
            >
              <button className="w-[60px] h-[60px] flex-shrink-0  flex items-center justify-center rounded-xl hover:bg-zinc-200">
                {renderSpeedIcon()}
              </button>
            </Dropdown>
            <Popover
              //parentElement={containerRef.current}
              isOpen={isGridPopoverOpen}
              positions={["top"]} // preferred positions by priority
              onClickOutside={() => setIsGridPopoverOpen(false)} // handle click events outside of the popover/target here!
              content={
                <div className="grid grid-cols-3 gap-3 border rounded-xl shadow-xl p-3 bg-white">
                  <button onClick={() => onLayoutChange(2)}>
                    <SvgStreamingLayout
                      layoutName="2x2"
                      width="60"
                      className={clsx(
                        "mb-1",
                        layout == 2 ? "fill-blue-400" : "fill-blue-100",
                      )}
                    />
                    <p className="text-sm">2x2</p>
                  </button>
                  <button onClick={() => onLayoutChange(3)}>
                    <SvgStreamingLayout
                      layoutName="3x3"
                      width="60"
                      className={clsx(
                        "mb-1",
                        layout == 3 ? "fill-blue-400" : "fill-blue-100",
                      )}
                    />
                    <p className="text-sm">3x3</p>
                  </button>
                  <button onClick={() => onLayoutChange(4)}>
                    <SvgStreamingLayout
                      layoutName="4x4"
                      width="60"
                      className={clsx(
                        "mb-1",
                        layout == 4 ? "fill-blue-400" : "fill-blue-100",
                      )}
                    />
                    <p className="text-sm">4x4</p>
                  </button>
                </div>
              }
            >
              <button
                className="w-[60px] h-[60px] flex items-center justify-center rounded-xl hover:bg-zinc-200"
                onClick={() => setIsGridPopoverOpen(!isGridPopoverOpen)}
              >
                <SvgGrid width="28" className="fill-neutral-800" />
              </button>
            </Popover>
            <button
              className="w-[60px] h-[60px] flex items-center justify-center rounded-xl hover:bg-zinc-200"
              onClick={() => {
                handleFullscreen(refPlayerContainer.current);
              }}
            >
              <SvgFullscreen width="28" className="fill-neutral-800" />
            </button>
            <Tooltip title={"View Playback"}>
              <button
                disabled={!curEventData}
                onClick={() => setPlaybackModalVisible(true)}
                className="w-[60px] h-[60px] flex items-center justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:fill-neutral-400 disabled:bg-transparent disabled:cursor-not-allowed"
              >
                <SvgPlayback width="25" />
              </button>
            </Tooltip>
            <Tooltip title={"Download Pic"}>
              <button
                disabled={!curEventData}
                onClick={handleDownloadPresetPic}
                className="w-[60px] h-[60px] flex items-center justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:fill-neutral-400 disabled:bg-transparent disabled:cursor-not-allowed"
              >
                <SvgDownload width="25" />
              </button>
            </Tooltip>
            <Tooltip title={"Previous Page"}>
              <button
                className="w-[60px] h-[60px] flex items-center justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:opacity-25 disabled:cursor-not-allowed"
                disabled={currentPage === 0}
                onClick={handlePreviousPage}
              >
                <SvgAlertPrevPage width="14" />
              </button>
            </Tooltip>
            <Tooltip title={"Next Page"}>
              <button
                className="w-[60px] h-[60px] flex items-center justify-center rounded-xl hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:opacity-25 disabled:cursor-not-allowed"
                disabled={!hasNextPage}
                onClick={handleNextPage}
              >
                <SvgAlertNextPage width="14" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <PlaybackModal
        orgId={+selectedCamera?.orgId}
        visible={playbackModalVisible}
        onClose={() => setPlaybackModalVisible(false)}
        cameraId={selectedCamera?.id}
        //alertTime={+curEventData?.createdAt}
        startAt={+curEventData?.createdAt - 1000 * 60 * 10}
        endAt={+curEventData?.createdAt + 1000 * 60 * 5}
        initialSeekAt={+curEventData?.createdAt}
      />
    </>
  );
}
